<template>
    <b-tab lazy title-item-class="mb-1" :title-link-class="{ 'bg-danger': hasErrors, 'bg-warning': isDirty}">
        <template slot="title">
            <i v-if="isDirty || hasErrors" class="fa-solid fa-circle-exclamation" :class="{ 'fa-circle-exclamation text-white': hasErrors, 'fa-cloud-arrow-up text-black': isDirty}"></i> Diagnostic Categories
        </template>
        <b-form-group
            label="Diagnostic Categories"
            label-size="lg"
            label-class="font-weight-bold pt-0">
            <validation-observer ref="observer" v-slot="{ passes }">
                <b-form @submit.prevent="passes(onFormSubmit)" novalidate>
                    <b-overlay 
                        :show="$store.getters['loading/isLoading']('diagnosticCategories')"
                        rounded="sm"
                        variant="light"
                        :opacity="0.80"
                        blur="2px">
                        <b-row>
                            <b-col>
                                <b-card no-body>
                                    <template v-slot:header>
                                        <span class="align-middle">Available Diagnostic Categories</span>
                                        <div class="card-header-actions">
                                            <b-button size="sm" variant="primary" @click="showNewCategoryModal">
                                                <i class="fa-solid fa-plus"></i> New Category
                                            </b-button>
                                        </div>
                                    </template>
                                    <b-list-group flush>
                                        <b-list-group-item class="p-2 flex-column align-items-start" v-for="(element, index) in available" :key="element.id">
                                            <div class="d-flex w-100 justify-content-between">
                                                <UpdateDiagnosticCategory :item="element" />
                                                <b-link @click="add(element)">
                                                    <i class="fa-solid fa-plus align-middle"></i>
                                                </b-link>
                                            </div>
                                        </b-list-group-item>
                                        <b-list-group-item class="text-center" v-if="noAvailableDiagnosticCategories"><i class="fa-solid fa-ban fa-xl"></i></b-list-group-item>
                                    </b-list-group>
                                </b-card>
                            </b-col>
                            <b-col>
                                <b-card no-body>
                                    <template slot="header">
                                        <span class="align-middle">Activated Diagnostic Categories</span>
                                        <div class="card-header-actions">
                                            <b-button size="sm" class="mr-1" type="submit" variant="success"><i class="fa-regular fa-circle-dot"></i> Submit</b-button>
                                            <b-button size="sm" v-show="isDirty | hasErrors" type="button" variant="danger" @click="reset"><i class="fa-solid fa-ban"></i> Reset</b-button>
                                        </div>
                                    </template>
                                    <b-list-group flush>
                                        <b-list-group-item class="p-2 flex-column align-items-start" v-for="(element, index) in selected" :key="element.id">
                                            <div class="d-flex w-100 justify-content-between">
                                                <UpdateDiagnosticCategory :item="element" />
                                                <b-link @click="removeAt(index)">
                                                    <i class="fa-solid fa-minus align-middle"></i>
                                                </b-link>
                                            </div>
                                        </b-list-group-item>
                                        <b-list-group-item class="text-center" v-if="noSelectedDiagnosticCategories">Add diagnostic category</b-list-group-item>
                                    </b-list-group>
                                </b-card>
                            </b-col>
                        </b-row>
                    </b-overlay>
                   <b-form-group>

                    </b-form-group>
                </b-form>
            </validation-observer>
        </b-form-group>
    </b-tab>
</template>
<script>
import Vue from "vue"

import FormExtension from "@/core/components/FormExtension"
import { mapGetters, mapState, mapActions } from "vuex"

import NewDiagnosticCategoryModal from './NewDiagnosticCategoryModal'
import UpdateDiagnosticCategory from './UpdateDiagnosticCategory'

export default FormExtension.extend({
    name: 'user-diagnostic-categories',
    components: {
        UpdateDiagnosticCategory
    },
    data() {
        return {
            form: new Form({
                diagnostic_category_types: [],
                consistency_token: null
            }),

            available: [],
            selected: [],

            isDirty: false,
            hasErrors: false,
        }
    },
    computed: {
        ...mapGetters('catalogue', ['getSettings']),
        ...mapState('catalogue', {
            categories: state => state.diagnostic_categories
        }),
        settings() {
            return this.getSettings()
        },
        noAvailableDiagnosticCategories() {
            return this.available.length < 1
        },
        noSelectedDiagnosticCategories() {
            return this.selected.length < 1
        }
    },
    watch: {
        settings: {
            immediate: true,
            handler(val) {
                this.form.withData(val)
            }
        },

        categories: {
            immediate: true,
            handler(val) {
                this.loadCategories()
            }
        },
    },
    methods: {
        ...mapActions('catalogue', ['getCatalogueDefaults', 'updateCatalogueSettings', 'getCatalogueItem', 'reloadCatalogueItem']),
        async onItem() {
            await this.reloadCatalogueItem('diagnostic_categories')
        },
        showNewCategoryModal() {
            var ComponentClass = Vue.extend(NewDiagnosticCategoryModal)
            var instance = new ComponentClass({
                parent: this
            })

            instance.$on("hidden", () => {
                instance.$destroy()
            })

            instance.$on("created", () => {
                this.onItem()
            })

            instance.$mount()
        },
        onFormSubmit() {
            const categories = this.selected.map(category => category.id)
            this.form.populate({diagnostic_category_types: categories})
            this.updateCatalogueSettings(this.form.data()).then((settings) => {
                this.form.withData(settings)
                this.reset()
                this.notify('Diagnostic categories updated', "Diagnostic categories were updated successfully")
            })
            .catch(errors => {
                if (errors.response) {
                    this.$refs.observer?.setErrors(errors.response.data)
                    this.hasErrors = true
                }
            })
        },
        removeAt(idx) {
            this.selected.splice(idx, 1)
            this.isDirty = true
            this.available = this.categories.filter(event => !this.selected.includes(event))
        },
        add(element) {
            this.selected.push(element)
            this.isDirty = true
            this.available = this.categories.filter(event => !this.selected.includes(event))
        },
        reset() {
            this.loadCategories()
            this.$nextTick(() => {
                this.$refs.observer?.reset()
                this.hasErrors = false
                this.isDirty = false
                this.validated()
            })
        },
        loadCategories() {
            console.log("loadCategories")
            const ids = this.form.diagnostic_category_types || []

            this.available = this.categories.filter(event => !ids.includes(event.id))
            this.selected = this.categories.filter(event => ids.includes(event.id))
        }

    },
    async mounted() {
        await this.getCatalogueItem('diagnostic_categories')
    }
})
</script>
